import { useMemo } from 'react';
import { useCornDensityCurveForProducts } from '/@/hooks/recommendation';
import { type CurvePoint, environmentsFor, toSimulationSnapshot } from './density-simulation-data';
import { CURVE_RANGE, DENSITY_ANCHOR, type ManagementHybrid } from './use-management-data';

type CurveResponse = Record<number, { points?: CurvePoint[] } | undefined>;

/** The endpoint's density scale → plants/ha, the same conversion `useManagementData` does. */
const pointsFor = (data: unknown, productId?: number) =>
    productId === undefined
        ? undefined
        : (data as CurveResponse | undefined)?.[productId]?.points?.map((point) => ({
              density: point.density * DENSITY_ANCHOR,
              yield: point.yield,
          }));

/**
 * The density/environment simulation for one hybrid, fetched live.
 *
 * The estimated environment's curve is already on the hybrid; the shifted environments need a
 * curve each, requested only for the hybrid on screen. The percentages should be committed
 * values, not the one under a slider thumb, or every drag step becomes two requests.
 */
export const useDensitySimulation = ({
    hybrid,
    environment,
    betterPercent,
    worsePercent = betterPercent,
    habitualDensity,
}: {
    hybrid?: ManagementHybrid;
    environment?: number;
    betterPercent: number;
    worsePercent?: number;
    /** Plants/ha. */
    habitualDensity?: number;
}) => {
    const environments = useMemo(
        () => (environment ? environmentsFor(environment, betterPercent, worsePercent) : undefined),
        [environment, betterPercent, worsePercent]
    );
    const productsId = hybrid ? [hybrid.id] : undefined;

    const overQuery = useCornDensityCurveForProducts({
        productsId,
        environment: environments?.over,
        ...CURVE_RANGE,
        crop: 'corn',
    });
    const underQuery = useCornDensityCurveForProducts({
        productsId,
        environment: environments?.under,
        ...CURVE_RANGE,
        crop: 'corn',
    });

    const over = useMemo(() => pointsFor(overQuery.data, hybrid?.id), [overQuery.data, hybrid?.id]);
    const under = useMemo(() => pointsFor(underQuery.data, hybrid?.id), [underQuery.data, hybrid?.id]);

    const snapshot = useMemo(
        () =>
            hybrid
                ? toSimulationSnapshot({
                      hybridName: hybrid.catalogName,
                      environment,
                      betterPercent,
                      worsePercent,
                      recommendedDensity: hybrid.harvestDensity,
                      recommendedYield: hybrid.estimatedYield,
                      habitualDensity,
                      curves: { over, estimated: hybrid.points, under },
                  })
                : undefined,
        [hybrid, environment, betterPercent, worsePercent, habitualDensity, over, under]
    );

    return { snapshot, isLoading: overQuery.isLoading || underQuery.isLoading };
};
